import React, { useEffect, useRef } from "react";
import { MOTION } from "../styles/motionTokens";
import ParticleBurst from "../styles/ParticleBurst";
import { Z } from "../styles/zLayers";
import { useDismissible } from "../native/dismissibleStack";

/**
 * Full mutual-completion celebration only — never for one-sided confirm.
 * Auto-dismisses after celebrationMs; caller owns open state.
 */
const TradeCompletionMoment = ({ open, onClose, mineSrc, theirsSrc }) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => onClose?.(), MOTION.celebrationMs);
    return () => clearTimeout(t);
  }, [open, onClose]);

  useDismissible(open, onClose, "trade-completion-moment");

  /**
   * Focus management, because this really is an `aria-modal` dialog.
   *
   * It declares `aria-modal="true"`, which asks assistive tech to treat
   * everything outside as hidden, and then moved focus nowhere — so a keyboard
   * or screen-reader user was left on `<body>`, outside a dialog that had just
   * declared everything outside itself invisible, with nothing announced on
   * open.
   *
   * CORRECTED against an earlier draft of this comment, which said the user
   * "stayed parked on whatever they were standing on when the trade completed".
   * That mechanism is wrong and was measured wrong: the control they pressed is
   * already unmounted before this dialog exists, by the reload the confirm
   * handler awaits (`TradeJourneyPage.jsx` gates the whole page on `loading`;
   * `TradesPage` unmounts the row with its list loader), so `activeElement` is
   * `<body>` by the time we get here, not the opener. The harm is identical
   * either way — and this is also precisely why the restore arm below is
   * absent. Same remedy and same shape as
   * `MatchRevealMoment` (effect at :260-276, `tabIndex={-1}` at :337) and
   * `ProfileAccountSheet` (effect at :146-168, `tabIndex={-1}` at :209).
   *
   * The ROOT takes focus rather than the "Done" button: this is a "look at
   * this" beat that also auto-dismisses, and arriving pre-armed under Enter on
   * a control that closes the celebration would throw it away on a stray press.
   *
   * ONLY HALF OF THE PRECEDENT IS HERE, DELIBERATELY. Both of those components
   * also restore focus to the opener on close. THIS ONE HAS NO OPENER TO
   * RESTORE TO, and that was measured rather than assumed. Both call sites open
   * it from the tail of an async confirm handler that then re-reads the trade:
   * `setCelebration(...)` at `TradeJourneyPage.jsx:187`, inside `onConfirm`
   * (`:166`), and at `TradesPage.jsx:521`, inside `handleAction` (`:487`).
   *
   * WHAT UNMOUNTS THE PRESSED BUTTON IS THE RE-READ'S LOADING GATE, NOT THE
   * STATUS IT RETURNS — corrected here after being measured; the sentence this
   * replaces named the status change and that is not the mechanism. `onConfirm`
   * finishes with a NON-silent `await load()` (`:199`), `load` sets
   * `setLoading(true)` unless called `{silent: true}` (`:123`), and
   * `TradeJourneyPage.jsx:211-216` then replaces the WHOLE page — Confirm
   * included — with a `<Loader context="panel">`. That happens whatever status
   * comes back; the refusal branch above it is the one that re-reads silently
   * (`:177`) and it returns before any celebration.
   *
   * The conclusion is unchanged and is what the absent arm rests on: driving
   * the real `TradeJourneyPage` through that flow, `document.activeElement` is
   * already `<body>` by the time this dialog is on screen and the confirm button
   * is no longer in the document. A restore arm copied from the precedent would
   * short-circuit on its own `opener !== document.body` guard every time — it
   * would be decoration, and no test could make it fail.
   *
   * THIS IS NOT A FOCUS TRAP AND MUST NOT BE READ AS ONE. Moving focus in is
   * the announce fix; containing Tab, and giving the user somewhere meaningful
   * to land on close, are separate and are filed rather than claimed here.
   */
  useEffect(() => {
    if (!open) return;
    // The root is a <div>, so this only does anything because of the
    // `tabIndex={-1}` below; without it focus() is a silent no-op.
    dialogRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      ref={dialogRef}
      data-testid="trade-completion-moment"
      data-motion="binder-snap"
      role="dialog"
      aria-modal="true"
      aria-label="Trade complete"
      // A <div> is not focusable without this, so the effect above would call
      // focus() to no effect and leave the user exactly where they were. -1 so
      // the root can hold focus without becoming a Tab stop of its own.
      tabIndex={-1}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: Z.celebration,
        background: "rgba(15,23,42,0.72)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 340,
          background: "#0f172a",
          borderRadius: 20,
          padding: "28px 20px 24px",
          textAlign: "center",
          border: "1px solid rgba(139,92,246,0.35)",
          boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
          animation: `tradeSnapIn ${MOTION.binderSnapMs}ms cubic-bezier(0, 0, 0.2, 1)`,
        }}
      >
        <div
          data-testid="trade-snap-cards"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            marginBottom: 16,
            position: "relative",
          }}
        >
          {/* Same burst the propose moment uses, dialled down — completion is
              the quieter, more certain of the two beats. */}
          <ParticleBurst variant="sparkle" count={18} seed={5} />
          <img
            src={mineSrc || "https://placehold.co/72x100?text=You"}
            alt="Your card"
            style={{
              width: 72,
              height: 100,
              borderRadius: 8,
              objectFit: "cover",
              boxShadow: "0 0 0 2px rgba(139,92,246,0.7), 0 8px 20px rgba(109,40,217,0.35)",
              animation: `tradeSlideRight ${MOTION.binderSnapMs}ms ease`,
            }}
          />
          <span
            data-testid="trade-snap-label"
            style={{
              position: "absolute",
              fontSize: 22,
              fontWeight: 900,
              letterSpacing: "0.08em",
              color: "#c4b5fd",
              textShadow: "0 0 18px rgba(167,139,250,0.9)",
              animation: `tradeFoilFlash ${MOTION.foilFlashMs}ms ease ${MOTION.binderSnapMs * 0.35}ms both`,
            }}
          >
            SNAP
          </span>
          <img
            src={theirsSrc || "https://placehold.co/72x100?text=Them"}
            alt="Their card"
            style={{
              width: 72,
              height: 100,
              borderRadius: 8,
              objectFit: "cover",
              boxShadow: "0 0 0 2px rgba(139,92,246,0.7), 0 8px 20px rgba(109,40,217,0.35)",
              animation: `tradeSlideLeft ${MOTION.binderSnapMs}ms ease`,
            }}
          />
        </div>

        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            margin: "0 auto 12px",
            background: "#6d28d9",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            fontWeight: 800,
          }}
          aria-hidden
        >
          ✓
        </div>
        <div style={{ color: "#c4b5fd", fontWeight: 800, fontSize: 18, marginBottom: 4 }}>Trade Complete</div>
        <div style={{ color: "#94a3b8", fontSize: 13 }}>Both of you confirmed the swap.</div>
        <button
          type="button"
          data-testid="trade-completion-dismiss"
          onClick={onClose}
          style={{
            marginTop: 18,
            width: "100%",
            padding: "12px 16px",
            borderRadius: 10,
            border: "none",
            background: "#6d28d9",
            color: "#fff",
            fontWeight: 700,
            fontSize: 14,
            cursor: "pointer",
            fontFamily: "Inter, sans-serif",
          }}
        >
          Done
        </button>
      </div>

      <style>{`
        @keyframes tradeSnapIn {
          from { transform: translateY(16px) scale(0.96); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes tradeSlideRight {
          from { transform: translateX(-18px); opacity: 0.4; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes tradeSlideLeft {
          from { transform: translateX(18px); opacity: 0.4; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes tradeFoilFlash {
          0% { opacity: 0; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.08); }
          100% { opacity: 0.85; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default TradeCompletionMoment;
